// ── Paginação ─────────────────────────────────────────────────────────────

export interface PaginadoResponse<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

// ── Posição de Estoque ────────────────────────────────────────────────────

export interface EstoquePosicaoItem {
  subProdutoId: number;
  produtoId: number;
  descricao: string;
  codigoBarras?: string;
  localEstoqueId: number;
  localDescricao: string;
  saldoAtual: number;
  saldoReservado: number;
  estoqueMinimo: number;
  estoqueMaximo: number;
  custoMedio: number;
}

export interface ReposicaoItem {
  subProdutoId: number;
  produtoId: number;
  descricao: string;
  localEstoqueId: number;
  localDescricao: string;
  saldoAtual: number;
  estoqueMinimo: number;
  quantidadeRepor: number;
}

// ── Movimentação de Estoque ───────────────────────────────────────────────

export const TIPOS_MOVIMENTO = [
  { id: 1, descricao: 'Entrada' },
  { id: 2, descricao: 'Saída' },
  { id: 3, descricao: 'Transferência' },
  { id: 4, descricao: 'Ajuste Positivo' },
  { id: 5, descricao: 'Ajuste Negativo' },
] as const;

export interface MovimentacaoResumo {
  id: number;
  tipoMovimentoId: number;
  tipoMovimentoDesc: string;
  numDocumento?: string;
  justificativa?: string;
  totalMovimentado: number;
  dtMovimento: string;
  usuarioId: number;
  dtCriacao: string;
}

export interface MovimentacaoProduto {
  id: number;
  subProdutoId: number;
  produtoDescricao: string;
  qtdEntrada: number;
  qtdSaida: number;
  saldoAtualEntrada: number;
  saldoAtualSaida: number;
  valorUnitario: number;
  valorTotal: number;
  localEntradaId: number;
  localSaidaId: number;
}

export interface MovimentacaoDetalhe extends MovimentacaoResumo {
  itens: MovimentacaoProduto[];
}

export interface MovimentacaoProdutoRequest {
  subProdutoId: number;
  qtdEntrada: number;
  qtdSaida: number;
  valorUnitario: number;
  localEntradaId: number;
  localSaidaId: number;
}

export interface CriarMovimentacaoRequest {
  tipoMovimentoId: number;
  dtMovimento: string;
  itens: MovimentacaoProdutoRequest[];
  numDocumento?: string;
  justificativa?: string;
  centroCustoId?: number;
  clienteId?: number;
}

// ── Inventário ────────────────────────────────────────────────────────────

export interface InventarioResumo {
  id: number;
  inventarioId: number;
  numDocumento?: string;
  dtMovimento: string;
  processado: boolean;
  totalItens: number;
  dtCriacao: string;
}

export interface InventarioItem {
  id: number;
  subProdutoId: number;
  produtoDescricao: string;
  quantidade: number;
  saldoSistema: number;
  divergencia: number;
  dtMovimento: string;
  status: boolean;
}

export interface InventarioDetalhe extends InventarioResumo {
  itens: InventarioItem[];
}

export interface CriarInventarioRequest {
  dtMovimento: string;
  numDocumento?: string;
}

export interface ContagemItemRequest {
  subProdutoId: number;
  quantidade: number;
}

export interface LancarContagemRequest {
  itens: ContagemItemRequest[];
}

// ── Local Estoque ─────────────────────────────────────────────────────────

export interface LocalEstoque {
  id: number;
  descricao: string;
  referencia?: string;
  estoqueLocal: boolean;
  estoqueDeTerceiros: boolean;
  estoquePoderTerceiros: boolean;
  isLocalPadrao: boolean;
  isInativo: boolean;
  centroCustoId?: number;
}

export interface LocalEstoqueCombo {
  id: number;
  descricao: string;
  referencia?: string;
  isLocalPadrao: boolean;
}

export type SalvarLocalEstoqueRequest = Omit<LocalEstoque, 'id'>;

// ── Relatórios de Estoque ─────────────────────────────────────────────────

export interface EstoqueAnaliticoItem {
  movimentoId: number;
  produtoId: number;
  produtoDescricao: string;
  subProdutoId: number;
  localEstoqueId: number;
  localDescricao: string;
  tipoMovimentoId: number;
  tipoMovimentoDesc: string;
  justificativa?: string;
  qtdMovimento: number;
  qtdSaldoAnterior: number;
  qtdSaldoAtual: number;
  dtAtualizacao: string;
}

export interface EstoqueGiroItem {
  produtoId: number;
  produtoDescricao: string;
  localEstoqueId: number;
  localDescricao: string;
  totalEntradas: number;
  totalSaidas: number;
  saldoAtual: number;
  giroEstoque: number;
}

// ── Gestão de Lotes ───────────────────────────────────────────────────────

export interface LoteResumo {
  id: number;
  produtoId: number;
  produtoDescricao: string;
  subProdutoId: number;
  quantidadeLote?: number;
  saldoLote?: number;
  dtFabricacao?: string;
  dtValidade?: string;
  precoMaximo?: number;
  /** "normal" | "vencendo" | "vencido" */
  status: string;
}

export interface CriarLoteRequest {
  produtoId: number;
  subProdutoId?: number;
  quantidadeLote?: number;
  saldoLote?: number;
  dtFabricacao?: string;
  dtValidade?: string;
  precoMaximo?: number;
}

export interface AtualizarLoteRequest {
  quantidadeLote?: number;
  saldoLote?: number;
  dtFabricacao?: string;
  dtValidade?: string;
  precoMaximo?: number;
}
